import { useEffect, useRef, useState } from 'react';

type UseInViewOptions = {
  rootMargin?: string;
  threshold?: number | number[];
  triggerOnce?: boolean;
};

type UseInViewResult<T extends HTMLElement> = { ref: (node: T | null) => void; inView: boolean };

export const useInView = <T extends HTMLElement>({
  rootMargin = '200px 0px',
  threshold = 0,
  triggerOnce = true,
}: UseInViewOptions = {}): UseInViewResult<T> => {
  const [inView, setInView] = useState(false);
  const nodeRef = useRef<T | null>(null);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (triggerOnce && inView) return;
    const node = nodeRef.current;
    if (!node) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          if (triggerOnce) {
            observer.disconnect();
          }
          return;
        }
        if (!triggerOnce) {
          setInView(false);
        }
      },
      { rootMargin, threshold },
    );

    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [inView, rootMargin, threshold, triggerOnce]);

  return {
    ref: (node) => {
      nodeRef.current = node;
    },
    inView,
  };
};
